import css from "./walletCard.module.css";
import { Alert, Dialog, IconInfo, Spinner, Text } from "@idg-web-platform/pulse";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import QRCode from "qrcode";

type DevicePlatform = "apple" | "android" | "desktop";
type WalletTarget = "apple" | "google";

function detectDevicePlatform(): DevicePlatform {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return "apple";
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "apple";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

type QrUrlsResponse = { appleUrl?: string; googleUrl?: string };

export interface WalletCardSignedUrlProps {
  appleWalletEnabled?: boolean;
  googleWalletEnabled?: boolean;
}

/**
 * New CON-1224 tile: per-platform "Add to Wallet" buttons. On desktop, clicking
 * a button fetches a freshly-signed URL from /api/c/club/wallet/qr-urls and
 * renders it as a QR for the user to scan with their phone. On a phone (UA
 * detected), the button navigates directly to the session-authed endpoint.
 */
export function WalletCardSignedUrl({
  appleWalletEnabled,
  googleWalletEnabled,
}: WalletCardSignedUrlProps) {
  const [devicePlatform, setDevicePlatform] = useState<DevicePlatform>("desktop");
  const [openModalFor, setOpenModalFor] = useState<WalletTarget | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const closeModal = useCallback(() => setOpenModalFor(null), []);

  useEffect(() => {
    setDevicePlatform(detectDevicePlatform());
  }, []);

  useEffect(() => {
    if (openModalFor) {
      dialogRef.current?.showModal?.();
    } else {
      dialogRef.current?.close?.();
      setQrDataUrl(null);
    }
  }, [openModalFor]);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.addEventListener("close", closeModal);
    return () => {
      dialog?.removeEventListener("close", closeModal);
    };
  }, [closeModal]);

  const openModal = async (target: WalletTarget) => {
    setError(false);
    setOpenModalFor(target);
    setQrDataUrl(null);
    try {
      const response = await fetch(`/api/c/club/wallet/qr-urls?platform=${target}`, {
        credentials: "same-origin",
      });
      if (!response.ok) {
        setError(true);
        return;
      }
      const data: QrUrlsResponse = await response.json();
      const url = target === "apple" ? data.appleUrl : data.googleUrl;
      if (!url) {
        setError(true);
        return;
      }
      const dataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 256,
      });
      setQrDataUrl(dataUrl);
    } catch {
      setError(true);
    }
  };

  if (!appleWalletEnabled && !googleWalletEnabled) {
    return null;
  }

  return (
    <div className={css.walletCard}>
      <div className={css.walletCardHeader}>
        <Text sizeStep={0} fontStyle="bold" removeMargin>
          <span className={css.badge}>Neu</span> Kundenkarte im Wallet speichern
        </Text>
      </div>

      <Text
        text="Speichere deine Kundenkarte im Wallet für einen schnelleren Zugriff im Store."
        sizeStep={0}
        removeMargin
        class={css.description}
      />

      <div className={css.walletButtonsRow}>
        {appleWalletEnabled && (
          <button
            type="button"
            className={css.walletBadgeButton}
            onClick={() =>
              devicePlatform === "apple"
                ? (window.location.href = "/api/c/club/wallet/apple-pass")
                : openModal("apple")
            }
            aria-label="Add to Apple Wallet"
          >
            <AppleWalletBadge />
          </button>
        )}
        {googleWalletEnabled && (
          <button
            type="button"
            className={css.walletBadgeButton}
            onClick={() =>
              devicePlatform === "android"
                ? (window.location.href = "/api/c/club/wallet/google-pass-url")
                : openModal("google")
            }
            aria-label="Add to Google Wallet"
          >
            <GoogleWalletBadge />
          </button>
        )}
      </div>

      <div style={{ display: "contents" }}>
        {openModalFor && (
          <Dialog ref={dialogRef} role="dialog" class={css.walletDialog} onClose={closeModal}>
            <div className={css.dialogContent}>
              <Text
                text={
                  openModalFor === "apple"
                    ? "Apple Wallet auf dem Smartphone öffnen"
                    : "Google Wallet auf dem Smartphone öffnen"
                }
                sizeStep={1}
                fontStyle="bold"
                removeMargin
              />
              {/* The error lives inside the dialog: the tile behind it is inert and
                  covered by the backdrop, so a tile-level alert would be unreadable. */}
              {error ? (
                <div className={css.dialogAlert}>
                  <Alert
                    type="warning"
                    headline="Fehler"
                    description="Das hat leider nicht geklappt. Bitte versuche es später erneut."
                    showCloseButton={false}
                  />
                </div>
              ) : (
                <>
                  <Text
                    text="Scanne diesen QR-Code mit deinem Smartphone — der Pass wird direkt in dein Wallet gespeichert."
                    sizeStep={0}
                    removeMargin
                  />
                  <div className={css.infoNote}>
                    <IconInfo />
                    <Text
                      text="Der QR-Code ist 10 Minuten gültig."
                      sizeStep={-1}
                      removeMargin
                      class={css.infoText}
                    />
                  </div>
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      className={css.dialogQrCode}
                      alt="QR-Code zum Hinzufügen der Kundenkarte zum Wallet"
                    />
                  ) : (
                    <div className={css.dialogSpinner}>
                      <Spinner dimension="l" color="blue" aria-label="QR-Code wird geladen" />
                    </div>
                  )}
                </>
              )}
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function AppleLogoSvg() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GoogleLogoSvg() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.29 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

function AppleWalletBadge() {
  return (
    <span className={css.badgeContent}>
      <AppleLogoSvg />
      <span className={css.badgeText}>
        <span className={css.badgeSmall}>Add to</span>
        <span className={css.badgeLarge}>Apple Wallet</span>
      </span>
    </span>
  );
}

function GoogleWalletBadge() {
  return (
    <span className={css.badgeContent}>
      <GoogleLogoSvg />
      <span className={css.badgeText}>
        <span className={css.badgeSmall}>Add to</span>
        <span className={css.badgeLarge}>Google Wallet</span>
      </span>
    </span>
  );
}
